import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AsyncSelectOption } from "../../../../../../components/AsyncSelect";
import AsyncSelectScroll from "@/components/AsyncSelectScroll";
import CustomDatePicker from "../../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../../components/CustomTextArea";
import CustomTimePicker from "../../../../../../components/CustomTimePicker";
import ErrorHelper from "../../../../../../components/ErrorWrapper";
import Flex from "../../../../../../components/Flex";
import Text from "../../../../../../components/Text";
import ViewFileModal from "../../../../../../components/ViewFileModal";
import StyledFormModal from "../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../components/app/StyledButtonV2";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../../hooks/use-disclosure";
import { useDebounce } from "../../../../../../hooks/use-debounce";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import { useAddTask } from "../../../../../../queries/collaboration/tasks/use-add-task";
import { useTaskOptionsList } from "../../../../../../queries/collaboration/tasks/use-task-title-list";
import { useTaskTypeList } from "../../../../../../queries/collaboration/tasks/use-task-type-list";
import { useProvidersList } from "../../../../../../queries/provider/setting/use-providers-list";
import { usePatientsList } from "../../../../../../queries/use-get-patients";
import { formatDate } from "../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../../utils/utils";
import { TASK_PRIORITIES } from "../../../collabration/collaboration-task-constant";

interface AssignAndCreateTaskModalProps {
  disclosureHost: DisclosureHost;
}

interface AddToPatientChartForm {
  title: AsyncSelectOption | null;
  priority: string;
  patient: AsyncSelectOption | null;
  actions: AsyncSelectOption | null;
  dueDate: string | null;
  assignTo: AsyncSelectOption | null;
  description: string;
}

const validationSchema = Yup.object({
  title: Yup.object().required().label("Title"),
  priority: Yup.string().required().label("Priority"),
  patient: Yup.object().required().label("Patient"),
  actions: Yup.object().required().label("Action"),
  dueDate: Yup.string().required().label("Due Date/Time"),
  assignTo: Yup.object().required().label("Assignee"),
  description: Yup.string(),
});

const getFileUrl = (result: string | undefined): string | null => {
  if (!result) return null;
  if (result.startsWith("data:")) return result;
  return `data:application/pdf;base64,${result}`;
};

const AssignAndCreateTaskModal = ({
  disclosureHost,
}: AssignAndCreateTaskModalProps) => {
  const prefillDate = dayjs().add(1, "hour").toISOString();
  const viewAttachmentDisclosure = useDisclosure();

  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: createTask } = useAddTask();
  const [taskListSearch, setTaskListSearch] = useDebounce<string>("");
  const [primaryProviderSearch, setPrimaryProviderSearch] =
    useDebounce<string>("");
  const [patientSearch, setPatientSearch] = useDebounce<string>("");
  const [actionSearch, setActionSearch] = useDebounce<string>("");

  const [titlePagination, setTitlePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [actionPagination, setActionPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [patientPagination, setPatientPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [assigneePagination, setAssigneePagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const { data: providersList, isFetching: isProvidersFetching } =
    useProvidersList({
      status: true,
      pagination: assigneePagination,
      searchKey: primaryProviderSearch,
      enabled: disclosureHost.opened,
    });

  const { data: taskTypeActionList, isFetching: isTaskTypeFetching } =
    useTaskTypeList({
      gridSorting: [],
      pagination: actionPagination,
      searchString: actionSearch,
      xTENANTID: getXtenantId(),
      enabled: disclosureHost.opened,
    });

  const { data: taskOptionsList, isFetching: isTaskOptionsFetching } =
    useTaskOptionsList({
      pagination: titlePagination,
      gridSorting: [],
      status: true,
      archive: false,
      searchString: taskListSearch,
      xTENANTID: getXtenantId(),
    });

  const { data: patientList, isFetching: isPatientsFetching } = usePatientsList(
    {
      pagination: patientPagination,
      searchKey: patientSearch,
      providerGroupId: getXtenantId(),
      enabled: disclosureHost.opened,
    }
  );

  const taskListOptions =
    taskOptionsList?.data?.content?.map((it) => ({
      value: it?.uuid,
      label: it?.name,
    })) ?? [];

  const taskTypeActionOptions =
    taskTypeActionList?.data?.content.map((it) => ({
      value: it?.uuid,
      label: it?.title,
    })) ?? [];

  const formik = useFormik<AddToPatientChartForm>({
    initialValues: {
      title: null,
      priority: "MEDIUM",
      patient: null,
      actions: null,
      dueDate: prefillDate,
      assignTo: null,
      description: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const handleDateValue = (date: Date, type: "date" | "time") => {
    if (type === "time" && formik.values?.dueDate) {
      const preDate = new Date(formik.values.dueDate);
      preDate?.setHours(date.getHours(), date.getMinutes(), date.getSeconds());
      formik.setFieldValue("dueDate", preDate?.toISOString());
    } else {
      formik.setFieldValue("dueDate", date?.toISOString());
    }
  };

  const getObj = (obj: { value: string; label: string } | null) =>
    obj ? { [obj.value]: obj.label } : undefined;

  async function handleSubmit(values: AddToPatientChartForm) {
    const selectedPatient = getObj(
      values.patient as { value: string; label: string } | null
    );
    const actions = getObj(
      values.actions as { value: string; label: string } | null
    );
    const assignTo = getObj(
      values.assignTo as { value: string; label: string } | null
    );

    const payload = {
      task: {
        ...values,
        title: (values.title as any)?.label || "",
        patient: selectedPatient,
        actions: actions || {},
        assignTo: assignTo || {},
        dueDate: values.dueDate || "",
        attachment: disclosureHost.data?.result,
      },
      xTENANTID: getXtenantId(),
    };

    showToastAndResetForm(createTask(payload as any));
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    showToastOnApiCall({
      apiCall,
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["collaboration-task-list"],
        });
        handleClose();
      },
    });
  }

  function handleClose() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <>
      <StyledFormModal
        open={disclosureHost.opened}
        onClose={handleClose}
        dialogTitle="Assign and Create Task"
        dialogAction={
          <Flex width={"100%"} justifyContent={"end"} gap={1}>
            <StyledButtonV2
              label="Cancel"
              variant="outlined"
              onClick={handleClose}
            />
            <StyledButtonV2
              label="Done"
              variant="contained"
              onClick={formik.submitForm}
            />
          </Flex>
        }
        sx={{
          "& .MuiDialog-paper": {
            width: "800px",
            height: "56vh",
          },
        }}
      >
        <Flex gap={1.5} flexDirection={"column"} pt={2}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <AsyncSelectScroll
                  required
                  label="Title"
                  placeholder="Select Title"
                  onSearch={(val) => {
                    setTaskListSearch(val);
                    setTitlePagination((p) => ({ ...p, page: 0 }));
                  }}
                  options={taskListOptions}
                  value={formik.values.title}
                  onChange={(opt) => formik.setFieldValue("title", opt)}
                  error={(formik.touched?.title && formik.errors?.title) as any}
                  loading={isTaskOptionsFetching}
                  pagination={titlePagination}
                  setPagination={setTitlePagination}
                  totalElements={taskOptionsList?.data?.page?.totalElements}
                />
              </Grid>
              <Grid item xs={6}>
                <CustomSelect
                  required
                  name="priority"
                  handleBlur={formik.handleBlur}
                  value={formik.values.priority}
                  onChange={(val) => formik.setFieldValue("priority", val)}
                  label="Priority"
                  options={TASK_PRIORITIES}
                  placeholder="Select"
                  error={
                    (formik.touched.priority && formik.errors.priority) as any
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <AsyncSelectScroll
                  enableFilterOption
                  handleBlur={formik.handleBlur}
                  onSearch={(val) => {
                    setPatientSearch(val);
                    setPatientPagination((p) => ({ ...p, page: 0 }));
                  }}
                  placeholder="Search Patient"
                  label="Patient Name"
                  required
                  name="patient"
                  renderCustomLabel={(option) => (
                    <Flex flexDirection={"column"}>
                      <Text>{option.label}</Text>
                      <Text>
                        {option?.birthDate ? formatDate(option?.birthDate) : ""}
                      </Text>
                    </Flex>
                  )}
                  options={
                    patientList?.data?.content?.map((p) => {
                      return {
                        value: p.uuid,
                        label: `${p.firstName}  ${p.lastName}`,
                        birthDate: p?.birthDate,
                      };
                    }) ?? []
                  }
                  value={formik.values.patient as any}
                  onChange={(value) => {
                    formik.setFieldValue("patient", value);
                  }}
                  error={
                    (formik.touched?.patient && formik.errors?.patient) as any
                  }
                  loading={isPatientsFetching}
                  pagination={patientPagination}
                  setPagination={setPatientPagination}
                  totalElements={patientList?.data?.page?.totalElements}
                />
              </Grid>
              <Grid item xs={6}>
                <AsyncSelectScroll
                  required
                  name="actions"
                  handleBlur={formik.handleBlur}
                  value={formik.values.actions}
                  onChange={(val) => formik.setFieldValue("actions", val)}
                  label="Action"
                  options={taskTypeActionOptions}
                  placeholder="Select"
                  onSearch={(val) => {
                    setActionSearch(val);
                    setActionPagination((p) => ({ ...p, page: 0 }));
                  }}
                  error={
                    (formik.touched.actions && formik.errors.actions) as any
                  }
                  loading={isTaskTypeFetching}
                  pagination={actionPagination}
                  setPagination={setActionPagination}
                  totalElements={taskTypeActionList?.data?.page?.totalElements}
                />
              </Grid>
              <Grid item xs={6}>
                <ErrorHelper
                  error={
                    formik.touched?.dueDate && (formik.errors.dueDate as any)
                  }
                >
                  <Flex gap={1.5} width={"100%"}>
                    <Box width={"50%"}>
                      <CustomDatePicker
                        disableError
                        error={
                          formik.touched?.dueDate &&
                          (formik.errors.dueDate as any)
                        }
                        required
                        name="dueDate"
                        handleBlur={formik.handleBlur}
                        placeholder="Choose Date"
                        value={formik.values.dueDate}
                        minDate={dayjs(new Date())}
                        label="Due Date"
                        onChange={(val) =>
                          handleDateValue(dayjs(val)?.toDate(), "date")
                        }
                      />
                    </Box>
                    <Box width={"50%"}>
                      <CustomTimePicker
                        disableError
                        error={
                          formik.touched?.dueDate &&
                          (formik.errors.dueDate as any)
                        }
                        required
                        label="Due Time"
                        name="dueTime"
                        handleBlur={formik.handleBlur}
                        value={formik.values.dueDate}
                        onChange={(value) =>
                          handleDateValue(dayjs(value)?.toDate(), "time")
                        }
                      />
                    </Box>
                  </Flex>
                </ErrorHelper>
              </Grid>
              <Grid item xs={6}>
                <AsyncSelectScroll
                  required
                  name="assignTo"
                  handleBlur={formik.handleBlur}
                  label="Assignee"
                  options={providersList?.data?.content?.map((it) => ({
                    value: it?.uuid,
                    label: it?.firstName + " " + it?.lastName,
                  }))}
                  placeholder="Search Assignee"
                  onSearch={(val) => {
                    setPrimaryProviderSearch(val);
                    setAssigneePagination((p) => ({ ...p, page: 0 }));
                  }}
                  value={formik.values.assignTo as any}
                  onChange={(value) => {
                    formik.setFieldValue("assignTo", value);
                  }}
                  error={
                    (formik.touched?.assignTo && formik.errors?.assignTo) as any
                  }
                  loading={isProvidersFetching}
                  pagination={assigneePagination}
                  setPagination={setAssigneePagination}
                  totalElements={providersList?.data?.page?.totalElements}
                />
              </Grid>
              <Grid item xs={12}>
                {disclosureHost.data?.result && (
                  <Flex alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <Tooltip title="Show attachment">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const url = getFileUrl(disclosureHost.data?.result);
                          if (url) viewAttachmentDisclosure.open(url);
                        }}
                        aria-label="Show attachment"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Text fontSize="14px" color="text.secondary">
                      Show attachment
                    </Text>
                  </Flex>
                )}
              </Grid>

              <Grid item xs={12}>
                <CustomTextArea
                  minRows={3}
                  label="Task Description"
                  placeholder="Task Description"
                  onChange={formik.handleChange}
                  name={"description"}
                  value={formik.values.description}
                />
              </Grid>
            </Grid>
          </form>
        </Flex>
      </StyledFormModal>
      <ViewFileModal
        disclosureHost={viewAttachmentDisclosure}
        title="Show attachment"
        w="60vw"
        h="calc(100svh - 120px)"
      />
    </>
  );
};

export default AssignAndCreateTaskModal;
